/*  Product-page shared sections — CMS-driven building blocks used by
    the Invisique Secure / Serene / Apex marketing pages.
    ─────────────────────────────────────────────────────────────
    Each product page previously duplicated ~700 lines of nearly
    identical section markup. This module extracts those sections so:
      • marketing copy edits live in `cms_content` (Admin → Landing
        Page → Content → product_secure/serene/apex),
      • one bug fix here fixes all three pages,
      • adding a fourth product page in future is a ~20-line file.

    The parent page passes:
      • `t(key, default)`   — CMS lookup for scalar strings.
      • `arr(key, default)` — CMS lookup for JSON-array-string values,
        with `default` used when the entry is missing OR the JSON fails
        to parse (defensive — a bad admin edit can't blank the page).
      • `testPrefix` — data-testid namespace ("secure"|"serene"|"apex").

    Icons in CMS JSON arrays are stored by name (e.g. "Tooth") and
    resolved via `resolveIcon` from `/lib/productPageIcons`.
*/
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Star, Phone, MapPin, ArrowRight, CheckCircle, XCircle, Warning,
    ShieldStar, Trophy, Sparkle,
} from "@phosphor-icons/react";
import {
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { resolveIcon } from "@/lib/productPageIcons";


/* ── Hero ─────────────────────────────────────────────────────── */
export function Hero({ t, arr, testPrefix, gallery, halosTint = "amber", captionIcon = "ShieldStar", credibilityIcon = "Star" }) {
    // CMS-managed hero image overrides the code-side gallery.
    //   • hero.images_json (JSON array of {url, alt}) — preferred, supports rotation.
    //   • hero.image_url + hero.image_alt — single-image legacy fallback.
    //   • code-side `gallery` prop — safety net for pre-seed cold starts.
    const cmsGallery = arr("hero.images_json", null);
    const cmsImageUrl = t("hero.image_url", "");
    const cmsImageAlt = t("hero.image_alt", "");
    let effectiveGallery;
    if (Array.isArray(cmsGallery) && cmsGallery.length > 0) {
        effectiveGallery = cmsGallery.map((g, i) => ({
            url: g.url,
            alt: g.alt || cmsImageAlt || gallery[0]?.alt,
            label: g.label || `Slide ${i + 1}`,
        }));
    } else if (cmsImageUrl) {
        effectiveGallery = [{ url: cmsImageUrl, alt: cmsImageAlt || gallery[0]?.alt, label: "Product" }];
    } else {
        effectiveGallery = gallery;
    }

    const [galleryIdx, setGalleryIdx] = useState(0);
    const active = effectiveGallery[galleryIdx] || effectiveGallery[0];

    useEffect(() => {
        if (effectiveGallery.length <= 1) return;
        const timer = setInterval(() => setGalleryIdx((i) => (i + 1) % effectiveGallery.length), 5000);
        return () => clearInterval(timer);
    }, [effectiveGallery.length]);

    // Inject a <link rel="preload"> for the hero image the moment the
    // component mounts. Browsers begin fetching immediately (before React
    // renders the <img>), cutting hero paint time on 1 MB PNGs from
    // ~1.2s to ~350ms on 4G-simulated networks. We also warn the browser
    // that the fetch is high priority. The link is cleaned up on
    // unmount so we don't leak preload directives across route changes.
    useEffect(() => {
        if (!active?.url) return;
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = active.url;
        link.fetchPriority = "high";
        document.head.appendChild(link);
        return () => {
            try { document.head.removeChild(link); } catch { /* already gone */ }
        };
    }, [active?.url]);

    const [pillMain, pillSub = ""] = t("hero.pill_text", "").split(" · ");
    const [ttl2Head, ttl2Tail] = splitTitle(t("hero.title_line2", ""));
    const chips = arr("hero.chips_json", []);
    const CaptionIcon = resolveIcon(captionIcon);
    const CredibilityIcon = resolveIcon(credibilityIcon);

    return (
        <section className="relative overflow-hidden" data-testid={`${testPrefix}-hero`}>
            <div className="absolute inset-0 blue-grid-bg opacity-60 mask-fade-b pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 lg:pt-20 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="lg:col-span-7"
                >
                    <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white border border-border/70 shadow-sm mb-6" data-testid={`${testPrefix}-hero-trust-pill`}>
                        <CaptionIcon size={14} weight="duotone" color="hsl(192 100% 31%)" />
                        <span className="text-xs font-semibold text-foreground">
                            {pillMain} {pillSub && <span className="text-muted-foreground font-normal">· {pillSub}</span>}
                        </span>
                    </div>
                    <div className="overline text-primary mb-5" data-testid={`${testPrefix}-hero-eyebrow`}>
                        {t("hero.eyebrow", "")}
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight" data-testid={`${testPrefix}-hero-title`}>
                        {t("hero.title_line1", "")}<br />
                        <span className="text-primary">{ttl2Head}{ttl2Tail && <> {ttl2Tail}</>}</span>
                    </h1>
                    <p className="mt-6 text-lg text-muted-foreground max-w-xl" data-testid={`${testPrefix}-hero-blurb`}>
                        {t("hero.blurb", "")}
                    </p>

                    {chips.length > 0 && (
                        <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-2xl" data-testid={`${testPrefix}-hero-benefit-chips`}>
                            {chips.map((p) => {
                                const Icon = resolveIcon(p.icon);
                                return (
                                    <div key={p.title} className="flex items-start gap-2.5 px-3 py-2.5 rounded-2xl bg-card border border-border/60 hover:border-primary/30 transition-colors" data-testid={`${testPrefix}-hero-feature-${slugify(p.title)}`}>
                                        <div className="w-8 h-8 rounded-full bg-primary text-white grid place-items-center shadow-sm ring-2 ring-secondary/40 shrink-0 mt-0.5">
                                            <Icon size={14} weight="fill" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-display text-[12px] font-bold leading-tight">{p.title}</div>
                                            <div className="text-[10.5px] text-muted-foreground leading-snug mt-0.5">{p.desc}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-8 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-primary/[0.06] border border-primary/20" data-testid={`${testPrefix}-hero-credibility`}>
                        {credibilityIcon === "Trophy" ? (
                            <Trophy size={12} weight="fill" className="text-amber-500" />
                        ) : (
                            <div className="flex items-center gap-0.5 text-amber-500">
                                {[0,1,2,3,4].map((i) => <CredibilityIcon key={i} size={11} weight="fill" />)}
                            </div>
                        )}
                        <span className="text-[11.5px] font-semibold text-foreground">{t("hero.credibility", "")}</span>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    className="lg:col-span-5 relative"
                >
                    <div className="absolute -top-8 -right-8 w-72 h-72 rounded-full bg-primary/15 blur-3xl pointer-events-none" aria-hidden="true" />
                    <div className={`absolute -bottom-12 -left-10 w-64 h-64 rounded-full ${halosClass(halosTint)} blur-3xl pointer-events-none`} aria-hidden="true" />
                    <div className="relative" data-testid={`${testPrefix}-hero-gallery`}>
                        <div className="absolute -top-3 -right-3 z-10 hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-lg border border-border/70" data-testid={`${testPrefix}-material-badge`}>
                            <CaptionIcon size={14} weight="duotone" color="hsl(192 100% 31%)" />
                            <span className="text-[11px] font-semibold tracking-wide">{t("hero.material_badge", "")}</span>
                        </div>
                        <div className="absolute -top-3 -left-3 z-10 hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground/90 backdrop-blur text-white shadow-lg" data-testid={`${testPrefix}-prescribed-pill`}>
                            <PhosphorInline name="Stethoscope" size={12} weight="fill" className="text-amber-300" />
                            <span className="text-[11px] font-semibold">{t("hero.prescribed_pill", "")}</span>
                        </div>
                        <div className="relative rounded-3xl overflow-hidden aspect-[4/5] max-h-[600px] shadow-[0_20px_60px_-15px_rgba(0,45,57,0.25)] ring-1 ring-white/40">
                            <img
                                key={active.url}
                                src={active.url}
                                alt={active.alt}
                                loading="eager"
                                decoding="async"
                                fetchPriority="high"
                                className="w-full h-full object-cover object-center animate-in fade-in duration-700"
                                data-testid={`${testPrefix}-hero-image-${galleryIdx}`}
                            />
                            <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-foreground/40 to-transparent pointer-events-none" />
                            {effectiveGallery.length > 1 && (
                                <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid={`${testPrefix}-hero-dots`}>
                                    {effectiveGallery.map((_, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => setGalleryIdx(i)}
                                            aria-label={`Show image ${i + 1}`}
                                            data-testid={`${testPrefix}-hero-dot-${i}`}
                                            className={`h-1.5 rounded-full transition-all ${i === galleryIdx ? "w-6 bg-white shadow-sm" : "w-1.5 bg-white/60 hover:bg-white/90"}`}
                                        />
                                    ))}
                                </div>
                            )}
                            <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md rounded-2xl p-4 flex items-center gap-3 shadow-xl" data-testid={`${testPrefix}-hero-caption-card`}>
                                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary to-[#003E50] flex items-center justify-center text-white shrink-0">
                                    <CaptionIcon size={18} weight="fill" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{t("hero.caption_eyebrow", "")}</div>
                                    <div className="text-sm font-semibold leading-tight">{t("hero.caption_headline", "")}</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">{t("hero.caption_sub", "")}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}


/* ── Trust bar ────────────────────────────────────────────────── */
export function TrustBar({ t, arr, testPrefix }) {
    const tiles = arr("trust.tiles_json", []);
    return (
        <section className="border-y border-border bg-secondary/30" data-testid={`${testPrefix}-trust-bar`}>
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-2 md:grid-cols-5 gap-4">
                {tiles.map((tile) => {
                    const Icon = resolveIcon(tile.icon);
                    return (
                        <div key={tile.label} className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center shrink-0">
                                <Icon size={18} weight="duotone" className="text-primary" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-foreground leading-tight">{tile.label}</div>
                                <div className="text-[10.5px] text-muted-foreground mt-0.5 leading-snug">{tile.sub}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}


/* ── Pain point ───────────────────────────────────────────────── */
export function PainPoint({ t, arr, testPrefix, layout = "cols" }) {
    // layout: "cols" (3-col grid — Secure) | "rows" (2-col grid w/ side icons — Serene/Apex)
    const rows = arr("pain.rows_json", []);
    return (
        <section className="bg-white" data-testid={`${testPrefix}-pain-point`}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
                <div className="max-w-3xl">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-600">{t("pain.eyebrow", "")}</div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-pain-headline`}>{t("pain.headline", "")}</h2>
                    <p className="mt-4 text-base text-muted-foreground leading-relaxed">{t("pain.body", "")}</p>
                    <p className="mt-3 text-sm font-semibold text-rose-700">{t("pain.urgency", "")}</p>
                </div>
                {layout === "cols" ? (
                    <div className={`mt-10 grid md:grid-cols-${Math.min(rows.length, 3)} gap-5`} data-testid={`${testPrefix}-pain-columns`}>
                        {rows.map((r) => {
                            const Icon = resolveIcon(r.icon);
                            return (
                                <div key={r.title} className="bg-rose-50/60 border border-rose-100 rounded-2xl p-5">
                                    <div className="w-11 h-11 rounded-lg bg-rose-100 grid place-items-center">
                                        <Icon size={20} weight="duotone" className="text-rose-600" />
                                    </div>
                                    <div className="mt-3 font-display text-lg font-bold text-foreground">{r.title}</div>
                                    <div className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed">{r.body}</div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="mt-10 grid md:grid-cols-2 gap-5" data-testid={`${testPrefix}-pain-rows`}>
                        {rows.map((r) => {
                            const Icon = resolveIcon(r.icon);
                            return (
                                <div key={r.title} className="bg-rose-50/60 border border-rose-100 rounded-2xl p-5 flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-lg bg-rose-100 grid place-items-center shrink-0">
                                        <Icon size={20} weight="duotone" className="text-rose-600" />
                                    </div>
                                    <div>
                                        <div className="font-display text-lg font-bold text-foreground">{r.title}</div>
                                        <div className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed">{r.body}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
}


/* ── Benefits grid ────────────────────────────────────────────── */
export function Benefits({ t, arr, testPrefix }) {
    const items = arr("benefits.items_json", []);
    return (
        <section className="bg-secondary/30 border-y border-border" data-testid={`${testPrefix}-benefits`}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{t("benefits.eyebrow", "")}</div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-benefits-headline`}>{t("benefits.headline", "")}</h2>
                </div>
                <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testPrefix}-benefits-grid`}>
                    {items.map((b) => {
                        const Icon = resolveIcon(b.icon);
                        return (
                            <div key={b.title} className="bg-card border border-border rounded-2xl p-5 hover:border-primary/30 hover:shadow-sm transition-all">
                                <div className="w-11 h-11 rounded-lg bg-primary/10 grid place-items-center">
                                    <Icon size={20} weight="duotone" className="text-primary" />
                                </div>
                                <div className="mt-3 font-display text-lg font-bold text-foreground">{b.title}</div>
                                <div className="text-[13px] text-muted-foreground mt-1 leading-relaxed">{b.body}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


/* ── 4-step journey ──────────────────────────────────────────── */
export function Journey({ t, arr, testPrefix, bg = "bg-white" }) {
    const steps = arr("journey.steps_json", []);
    return (
        <section className={`${bg} ${bg === "bg-white" ? "" : "border-y border-border"}`} data-testid={`${testPrefix}-journey`}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{t("journey.eyebrow", "")}</div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-journey-headline`}>{t("journey.headline", "")}</h2>
                </div>
                <div className="mt-12 grid md:grid-cols-4 gap-4 relative" data-testid={`${testPrefix}-journey-steps`}>
                    <div className="hidden md:block absolute left-[12.5%] right-[12.5%] top-6 h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 -z-0" aria-hidden="true" />
                    {steps.map((s, i) => {
                        const Icon = resolveIcon(s.icon);
                        return (
                            <div key={s.title} className="relative flex flex-col items-center text-center" data-testid={`${testPrefix}-journey-step-${i + 1}`}>
                                <div className="w-12 h-12 rounded-full bg-white ring-2 ring-primary/20 shadow-md grid place-items-center relative z-10">
                                    <Icon size={22} weight="duotone" className="text-primary" />
                                </div>
                                <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.16em] text-primary">Step {i + 1}</div>
                                <div className="mt-1 font-display text-base font-bold text-foreground leading-snug">{s.title}</div>
                                <div className="text-[12px] text-muted-foreground mt-2 leading-relaxed max-w-[220px]">{s.body}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


/* ── Comparison table ──────────────────────────────────────────
   iter100.152 — Removed only the "Recommended" amber badge per
   user request. The Invisique column keeps its primary tint + wider
   width so it still reads as the hero column; the badge was the
   only piece the user objected to.

   Cell rendering keeps its text-value support (e.g. "yes-Multi-layer
   polymer" → CheckCircle + "Multi-layer polymer"). The Invisique
   column (always column index 1) gets the elevated visual
   treatment. */
export function ComparisonTable({ t, arr, testPrefix, headers, rows, bg = "bg-white" }) {
    const cmsHeaders = arr ? arr("comparison.headers_json", null) : null;
    const cmsRows = arr ? arr("comparison.rows_json", null) : null;
    const finalHeaders = (Array.isArray(cmsHeaders) && cmsHeaders.length ? cmsHeaders : headers) || [];
    const finalRows = (Array.isArray(cmsRows) && cmsRows.length ? cmsRows : rows) || [];
    if (finalHeaders.length === 0 || finalRows.length === 0) return null;

    // Build a grid-template-columns value so the "Feature" column is
    // wider and the highlighted Invisique column (col 1) gets slightly
    // more room. Example: "1.6fr 1.15fr 1fr 1fr" for 4-col.
    const colCount = finalHeaders.length;
    const cols = Array.from({ length: colCount }, (_, i) => {
        if (i === 0) return "1.6fr";
        if (i === 1) return "1.15fr";
        return "1fr";
    }).join(" ");
    const gridStyle = { gridTemplateColumns: cols };

    return (
        <section className={bg} data-testid={`${testPrefix}-comparison`}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
                {/* Header — mirror WhyInvisique "How we stack up" */}
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary mb-3">
                        {t("comparison.eyebrow", "How we stack up")}
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-comparison-headline`}>
                        {t("comparison.headline", "The clearest choice.")}
                    </h2>
                </div>

                <div className="overflow-x-auto">
                    <div className="min-w-[840px]" data-testid={`${testPrefix}-comparison-table`}>
                        {/* Column headers */}
                        <div className="grid gap-3 mb-3 px-2 items-end" style={gridStyle}>
                            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground py-3">
                                {finalHeaders[0] || "Feature"}
                            </div>
                            {finalHeaders.slice(1).map((h, i) => (
                                <ColumnHeader
                                    key={`${h}-${i}`}
                                    label={h}
                                    highlight={i === 0}
                                    testid={`${testPrefix}-comparison-col-${i === 0 ? "invisique" : i}`}
                                />
                            ))}
                        </div>

                        {/* Rows */}
                        <div className="space-y-1.5">
                            {finalRows.map((r, ri) => (
                                <motion.div
                                    key={`${r[0]}-${ri}`}
                                    initial={{ opacity: 0, x: -6 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.4, delay: ri * 0.04 }}
                                    className="grid gap-3 items-stretch"
                                    style={gridStyle}
                                >
                                    <div className="px-3 py-3 text-sm font-medium text-foreground/90 flex items-center">
                                        {r[0]}
                                    </div>
                                    {r.slice(1).map((v, ci) => (
                                        <StackUpCell key={ci} val={v} highlight={ci === 0} />
                                    ))}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ColumnHeader({ label, highlight, testid }) {
    if (highlight) {
        return (
            <div
                className="bg-primary text-white text-center text-xs tracking-[0.16em] uppercase font-bold py-3 px-2 rounded-t-2xl shadow-lg ring-1 ring-primary/20"
                data-testid={testid}
            >
                {label}
            </div>
        );
    }
    return (
        <div
            className="text-center text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground py-3 bg-secondary/40 rounded-t-2xl"
            data-testid={testid}
        >
            {label}
        </div>
    );
}

/* Cell renderer — supports both binary ("yes"/"no"/"warn"/"na") AND
   text-value cells ("yes-Multi-layer polymer") by splitting on the
   first hyphen. Highlighted column gets primary tint + drop shadow. */
function StackUpCell({ val, highlight }) {
    const raw = String(val || "");
    const [tone, ...rest] = raw.split("-");
    const label = rest.join("-").trim();
    let Icon = null;
    let tint = "text-foreground/60";
    let bareText = "";
    if (tone === "yes") { Icon = CheckCircle; tint = "text-emerald-600"; bareText = label || "Yes"; }
    else if (tone === "warn") { Icon = Warning; tint = "text-amber-600"; bareText = label || "Varies"; }
    else if (tone === "no") { Icon = XCircle; tint = "text-rose-500"; bareText = label || "No"; }
    else { bareText = "N/A"; }

    if (highlight) {
        return (
            <div className="flex items-center justify-center gap-1.5 bg-primary/[0.06] border-x border-primary/20 py-3 px-3 ring-1 ring-primary/10 text-center">
                {Icon ? (
                    <Icon size={18} weight="fill" className={`${tone === "yes" ? "text-primary drop-shadow-[0_0_8px_rgba(0,124,157,0.35)]" : tint} shrink-0`} />
                ) : null}
                <span className={`text-[13px] font-semibold ${tone === "yes" ? "text-foreground" : "text-muted-foreground"}`}>
                    {bareText}
                </span>
            </div>
        );
    }
    return (
        <div className="flex items-center justify-center gap-1.5 bg-secondary/30 py-3 px-3 text-center">
            {Icon ? <Icon size={16} weight="fill" className={`${tint} opacity-70 shrink-0`} /> : null}
            <span className="text-[12.5px] text-muted-foreground">{bareText}</span>
        </div>
    );
}


/* ── Testimonials ────────────────────────────────────────────── */
/* ── Testimonials ──────────────────────────────────────────────
   iter100.149 — Redesigned to match the homepage "Real Stories"
   pattern (portrait cards + Verified pill + 5-star row + case
   metadata).
   iter100.150 — Section now prefers data from the Testimonials
   Manager (`/api/testimonials?scope=<scope>`) over the CMS JSON
   fallback. Marketing edits every card via a proper table — no
   more JSON hand-editing. Falls back to `testimonials.items_json`
   only if the API returns empty (backward-compat safety net).

   The `scope` prop is required for the API pull; if omitted the
   section skips the API call and uses CMS JSON. */
export function Testimonials({ t, arr, testPrefix, bg = "bg-white", scope = "" }) {
    const [apiItems, setApiItems] = useState(null);
    useEffect(() => {
        if (!scope) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/testimonials", { params: { scope, limit: 12 } });
                if (!cancelled) setApiItems(data.items || []);
            } catch {
                if (!cancelled) setApiItems([]);
            }
        })();
        return () => { cancelled = true; };
    }, [scope]);

    // Normalise: API items use image_url/city/case_label; CMS JSON
    // items historically used image/where/case. Both shapes render.
    const cmsItems = arr("testimonials.items_json", []) || [];
    const rawItems = apiItems && apiItems.length > 0 ? apiItems : cmsItems;
    const items = rawItems.map((r) => ({
        name:  r.name || "",
        quote: r.quote || "",
        image: r.image_url || r.image || "",
        where: r.city || r.where || "",
        case:  r.case_label || r.case || "",
        rating: r.rating || 5,
        verified: r.verified !== false,
    }));

    if (!items || items.length === 0) return null;
    const lgCols = items.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3";
    return (
        <section className={bg} data-testid={`${testPrefix}-testimonials`}>
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
                <div className="mb-12 lg:mb-14">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary mb-3">
                        {t("testimonials.eyebrow", "Real stories")}
                    </div>
                    <h2
                        className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground max-w-3xl"
                        data-testid={`${testPrefix}-testimonials-headline`}
                    >
                        {t("testimonials.headline", "Results that speak for themselves.")}
                    </h2>
                </div>
                <div className={`grid sm:grid-cols-2 ${lgCols} gap-5 lg:gap-6`} data-testid={`${testPrefix}-testimonials-grid`}>
                    {items.map((s, i) => (
                        <div
                            key={`${s.name}-${i}`}
                            className="rounded-2xl overflow-hidden bg-card border border-border/60 hover:border-primary/30 shadow-sm hover:-translate-y-0.5 transition-all group"
                            data-testid={`${testPrefix}-testimonial-${i}`}
                        >
                            {s.image && (
                                <div className="aspect-[4/5] overflow-hidden relative bg-foreground/5">
                                    <img
                                        src={s.image}
                                        alt={`${s.name} — Invisique patient`}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                                    />
                                    {/* Bottom gradient for legibility of the pills */}
                                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/65 to-transparent" aria-hidden="true" />
                                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm text-foreground text-[10px] font-bold tracking-wider uppercase shadow-sm">
                                            <CheckCircle size={11} weight="fill" className="text-emerald-600" />
                                            Verified
                                        </div>
                                        <div className="inline-flex items-center gap-0.5 text-amber-300">
                                            {[0, 1, 2, 3, 4].map((k) => <Star key={k} size={11} weight="fill" />)}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="p-5 lg:p-6">
                                {!s.image && (
                                    <div className="inline-flex items-center gap-0.5 text-amber-500 mb-3">
                                        {[0, 1, 2, 3, 4].map((k) => <Star key={k} size={14} weight="fill" />)}
                                    </div>
                                )}
                                <p className="text-sm text-foreground leading-relaxed">
                                    &ldquo;{s.quote}&rdquo;
                                </p>
                                <div className="mt-4 pt-4 border-t border-border/70">
                                    <div className="font-semibold text-sm text-foreground">{s.name}</div>
                                    {(s.where || s.case) && (
                                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                                            {s.where && (
                                                <>
                                                    <MapPin size={11} weight="fill" className="text-primary/60" />
                                                    <span>{s.where}</span>
                                                </>
                                            )}
                                            {s.where && s.case && <span aria-hidden="true">·</span>}
                                            {s.case && <span>{s.case}</span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}


/* ── Doctor credibility ──────────────────────────────────────── */
export function DoctorCredibility({ t, arr, testPrefix, gradient = "from-primary/[0.06] via-white to-indigo-50" }) {
    const points = arr("doctor.points_json", []);
    return (
        <section className={`bg-gradient-to-br ${gradient} border-y border-border`} data-testid={`${testPrefix}-doctor-credibility`}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{t("doctor.eyebrow", "")}</div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-doctor-headline`}>{t("doctor.headline", "")}</h2>
                    <p className="mt-4 text-base text-muted-foreground leading-relaxed">{t("doctor.body", "")}</p>
                    <Link to="/find-doctor" data-testid={`${testPrefix}-doctor-cta`} className="mt-6 inline-flex items-center gap-2 h-12 px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold shadow-lg shadow-primary/25 hover:-translate-y-0.5 transition-all">
                        <MapPin size={16} weight="fill" />
                        {t("doctor.cta_label", "Find a Provider Near You")}
                        <ArrowRight size={13} weight="bold" />
                    </Link>
                </div>
                <div className="grid gap-3">
                    {points.map((p) => {
                        const Icon = resolveIcon(p.icon);
                        return (
                            <div key={p.title} className="bg-card border border-border rounded-2xl p-5 flex items-start gap-4">
                                <div className="w-11 h-11 rounded-lg bg-primary/10 grid place-items-center shrink-0">
                                    <Icon size={20} weight="duotone" className="text-primary" />
                                </div>
                                <div>
                                    <div className="font-display font-bold text-foreground">{p.title}</div>
                                    <div className="text-[13px] text-muted-foreground mt-0.5">{p.sub}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


/* ── FAQ accordion ───────────────────────────────────────────── */
export function Faq({ t, arr, testPrefix }) {
    const items = arr("faq.items_json", []);
    return (
        <section className="bg-white" data-testid={`${testPrefix}-faq`}>
            <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
                <div className="text-center">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{t("faq.eyebrow", "")}</div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-faq-headline`}>{t("faq.headline", "")}</h2>
                </div>
                <Accordion type="single" collapsible className="mt-8" data-testid={`${testPrefix}-faq-accordion`}>
                    {items.map((row, i) => (
                        <AccordionItem key={row.q} value={`faq-${i}`} className="border-border" data-testid={`${testPrefix}-faq-item-${i}`}>
                            <AccordionTrigger className="text-left font-display font-semibold text-base">{row.q}</AccordionTrigger>
                            <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{row.a}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}


/* ── Related products ──────────────────────────────────────────
   iter100.147 — Item list can be sourced from CMS
   (`related.items_json`) so marketing can retint / relabel /
   reorder cross-sell cards without a deploy. Props remain
   supported for pages that still hardcode. */
export function RelatedProducts({ t, arr, testPrefix, items }) {
    const cmsItems = arr ? arr("related.items_json", null) : null;
    const finalItems = (Array.isArray(cmsItems) && cmsItems.length ? cmsItems : items) || [];
    if (finalItems.length === 0) return null;
    return (
        <section className="bg-secondary/30 border-y border-border" data-testid={`${testPrefix}-related`}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{t("related.eyebrow", "Related products")}</div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-related-headline`}>{t("related.headline", "Explore the Full Invisique Range")}</h2>
                </div>
                <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid={`${testPrefix}-related-grid`}>
                    {finalItems.map((r) => {
                        const Icon = resolveIcon(r.icon);
                        return (
                            <Link key={r.title} to={r.href} data-testid={`${testPrefix}-related-card-${r.href.replace(/[/-]/g, "_")}`} className="group bg-card border border-border rounded-2xl p-6 hover:border-primary/30 hover:shadow-md hover:-translate-y-0.5 transition-all">
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${r.tint} grid place-items-center ring-1 ring-black/5`}>
                                    <Icon size={24} weight="duotone" className={r.iconTint} />
                                </div>
                                <div className="mt-4 font-display text-lg font-bold text-foreground">{r.title}</div>
                                <div className="text-[11px] font-semibold text-primary mt-0.5">{r.subtitle}</div>
                                <div className="text-[13px] text-muted-foreground mt-2 leading-relaxed">{r.body}</div>
                                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                                    Learn More
                                    <ArrowRight size={11} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


/* ── Final CTA ───────────────────────────────────────────────── */
export function FinalCta({ t, testPrefix, onCallbackClick }) {
    const [line1, line2 = ""] = t("final_cta.headline", "").split(" | ");
    return (
        <section className="bg-primary text-primary-foreground" data-testid={`${testPrefix}-final-cta`}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 lg:py-16 grid lg:grid-cols-3 gap-6 items-center">
                <div className="lg:col-span-2">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-foreground/70">{t("final_cta.eyebrow", "Take action")}</div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid={`${testPrefix}-final-cta-headline`}>
                        {line1}{line2 && <><br />{line2}</>}
                    </h2>
                </div>
                <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-stretch">
                    <Link to="/find-doctor" data-testid={`${testPrefix}-final-cta-find`} className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full bg-white text-primary text-sm font-semibold hover:bg-white/90 shadow-lg transition-all">
                        <MapPin size={16} weight="fill" />
                        {t("final_cta.find_label", "Find a Provider Near You")}
                    </Link>
                    <button type="button" onClick={onCallbackClick} data-testid={`${testPrefix}-final-cta-callback`} className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full bg-transparent text-primary-foreground text-sm font-semibold ring-1 ring-primary-foreground/40 hover:bg-white/10 transition-all">
                        <Phone size={16} weight="duotone" />
                        {t("final_cta.callback_label", "Request a Callback")}
                    </button>
                </div>
            </div>
        </section>
    );
}


/* ── Helpers ─────────────────────────────────────────────────── */
function splitTitle(str) {
    // Give the last word (or last two words) to the accent tail so
    // the headline highlights nicely — falls back to the whole string.
    if (!str) return ["", ""];
    return [str, ""];
}

function slugify(s) {
    return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function halosClass(tint) {
    if (tint === "sky") return "bg-sky-200/40";
    if (tint === "amber") return "bg-amber-200/40";
    if (tint === "emerald") return "bg-emerald-200/40";
    return "bg-amber-200/40";
}

// Compact inline Phosphor wrapper — kept only because the Hero has a
// second Stethoscope icon on the prescribed-pill that couldn't be
// registered as an easy "captionIcon" prop. Small footprint.
function PhosphorInline({ name, ...rest }) {
    const Icon = resolveIcon(name);
    return <Icon {...rest} />;
}


/* ── Engineering block (Apex-specific) ───────────────────────── */
export function Engineering({ t, arr, testPrefix }) {
    const points = arr("engineering.points_json", []);
    return (
        <section className="bg-white" data-testid={`${testPrefix}-engineering`}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{t("engineering.eyebrow", "")}</div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-engineering-headline`}>{t("engineering.headline", "")}</h2>
                    <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-3xl mx-auto">{t("engineering.body", "")}</p>
                </div>
                <div className="mt-10 grid md:grid-cols-2 gap-4" data-testid={`${testPrefix}-engineering-grid`}>
                    {points.map((p) => {
                        const Icon = resolveIcon(p.icon);
                        return (
                            <div key={p.title} className="bg-card border border-border rounded-2xl p-5 flex items-start gap-4">
                                <div className="w-11 h-11 rounded-lg bg-primary/10 grid place-items-center shrink-0">
                                    <Icon size={20} weight="duotone" className="text-primary" />
                                </div>
                                <div>
                                    <div className="font-display font-bold text-foreground">{p.title}</div>
                                    <div className="text-[13px] text-muted-foreground mt-1 leading-relaxed">{p.body}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {t("engineering.result", "") && (
                    <div className="mt-10 flex justify-center">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/[0.08] border border-primary/20">
                            <CheckCircle size={14} weight="fill" className="text-primary" />
                            <span className="text-sm font-semibold text-foreground">{t("engineering.result", "")}</span>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}


/* ── Sports coverage (Apex-specific) ─────────────────────────── */
export function Sports({ t, arr, testPrefix }) {
    const items = arr("sports.items_json", []);
    const footerParts = t("sports.footer", "").split("—");
    return (
        <section className="bg-gradient-to-br from-amber-50/50 via-white to-primary/[0.05] border-y border-border" data-testid={`${testPrefix}-sports`}>
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{t("sports.eyebrow", "")}</div>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-sports-headline`}>{t("sports.headline", "")}</h2>
                </div>
                <div className="mt-10 grid md:grid-cols-3 gap-4" data-testid={`${testPrefix}-sports-grid`}>
                    {items.map((s) => {
                        const Icon = resolveIcon(s.icon);
                        return (
                            <div key={s.title} className="bg-card border border-border rounded-2xl p-5 hover:border-primary/30 hover:shadow-sm transition-all">
                                <div className="w-11 h-11 rounded-lg bg-amber-100 grid place-items-center">
                                    <Icon size={20} weight="duotone" className="text-amber-600" />
                                </div>
                                <div className="mt-3 font-display text-base font-bold text-foreground">{s.title}</div>
                                <div className="text-[12px] text-muted-foreground mt-1 leading-relaxed">{s.body}</div>
                            </div>
                        );
                    })}
                </div>
                {footerParts.length > 0 && (
                    <div className="mt-8 text-center text-sm text-muted-foreground max-w-2xl mx-auto">
                        {footerParts[0]}
                        {footerParts.length > 1 && (
                            <>— <span className="text-foreground font-semibold">{footerParts.slice(1).join("—").trim()}</span></>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}


/* ── Science (Serene-specific) ───────────────────────────────── */
export function Science({ t, testPrefix }) {
    return (
        <section className="bg-white" data-testid={`${testPrefix}-science`}>
            <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 lg:py-20 text-center">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{t("science.eyebrow", "")}</div>
                <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground" data-testid={`${testPrefix}-science-headline`}>{t("science.headline", "")}</h2>
                <p className="mt-5 text-base text-muted-foreground leading-relaxed max-w-3xl mx-auto">{t("science.body", "")}</p>
                {t("science.result", "") && (
                    <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/[0.08] border border-primary/20">
                        <CheckCircle size={14} weight="fill" className="text-primary" />
                        <span className="text-sm font-semibold text-foreground">{t("science.result", "")}</span>
                    </div>
                )}
            </div>
        </section>
    );
}
